// SPDX-License-Identifier: AGPL-3.0-or-later

import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import SessionManager from '@app/features/platform/state/AuthSession';
import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('AccountReadyWork');

let pendingUserId: string | null = null;

function runAccountReadyWork(userId: string): void {
	void import('@app/features/experiment/state/ExperimentAssignments')
		.then((module) => module.default.start(userId))
		.catch((error) => {
			logger.warn('Failed to start experiment assignments after READY', error);
		});
	void import('@app/features/premium/commands/PremiumCommands')
		.then((module) => module.refreshPremiumState())
		.catch((error) => {
			logger.warn('Failed to refresh premium state after READY', error);
		});
	void import('@app/features/platform/state/LegacySharedContentReview')
		.then((module) => module.reviewLegacySharedContent())
		.catch((error) => {
			logger.warn('Failed to review migrated shared content after READY', error);
		});
	// LOCAL-ONLY (calendar-plan.md section 11): the calendar's first read, or after a drop its since_revision.
	void import('@app/features/calendar/state/Calendar')
		.then((module) => module.default.handleReady(userId))
		.catch((error) => {
			logger.warn('Failed to read the calendar after READY', error);
		});
	// LOCAL-ONLY (favorites-bookmarks-plan.md section 3): every favorite's last message, again after a reconnect.
	// LOCAL-ONLY (bump-plan.md section 3): the bump records, again after a reconnect.
	void import('@app/features/bump/state/ChannelBumps')
		.then((module) => module.default.handleReady())
		.catch((error) => {
			logger.warn('Failed to fetch the bump records after READY', error);
		});
	void import('@app/features/messaging/state/FavoritePreviews')
		.then((module) => module.default.handleReady())
		.catch((error) => {
			logger.warn('Failed to load the favorites previews after READY', error);
		});
	void import('@app/features/auth/passkey_migration/PasskeyMigration')
		.then((module) => module.default.handleGatewayReady(userId))
		.catch((error) => {
			logger.warn('Failed to run the passkey migration check after READY', error);
		});
}

export function scheduleAccountReadyWork(userId: string): void {
	if (AccountScopedWork.isSuspended) {
		pendingUserId = userId;
		return;
	}
	pendingUserId = null;
	runAccountReadyWork(userId);
}

AccountScopedWork.registerTransition({
	suspend: () => {
		pendingUserId = null;
	},
	resume: () => {},
	released: () => {
		const userId = pendingUserId;
		pendingUserId = null;
		if (userId === null || SessionManager.userId !== userId) {
			return;
		}
		runAccountReadyWork(userId);
	},
});
